import React, { useState } from 'react';
import * as styles from './ControlBarBacklinks.module.scss';
import Button from '../../components/Button';
import SVGIcon from '../../components/SVGIcon';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import { useAllGroupMap } from '../../groups/useAllGroups';
import userIcon from '../../icons/non-prefilled/12x12/accept.svg';
import groupIcon from '../../icons/prefilled/16x16/group.svg';
import topicIcon from '../../icons/prefilled/16x16/topic.svg';
import { useAllTopicMap } from '../../topic/useAllTopics';
import { useAllUsersMap } from '../../user/useAllUsers';
import exhaustiveMatchGuard from '../../utils/exhaustiveMatchGuard';
import groupPath from '../../utils/url/groupPath';
import postPath from '../../utils/url/postPath';
import topicPath from '../../utils/url/topicPath';
import userPath from '../../utils/url/userPath';
import HoverContentPreview from '../HoverContentPreview';
import { getPostIcon } from '../PostIcon';
import { useAllSlimPostMap } from '../useAllSlimPosts';

const POST_FRAGMENT = gql(`
    fragment ControlBarBacklinks_post on Post {
        id
        backlinks {
          ... on Post {
            id
          }
          ... on User {
            id
          }
          ... on Group {
            id
          }
          ... on Topic {
            id
          } 
        }
    }
`);

const useBacklinksFromPost = (post: DocumentType<typeof POST_FRAGMENT>) => {
  const allUsersMap = useAllUsersMap();
  const allSlimPostsMap = useAllSlimPostMap();
  const allTopicMap = useAllTopicMap();
  const allGroupMap = useAllGroupMap();

  return post.backlinks
    .filter((backlink) => backlink.id !== post.id)
    .map((backlink) => {
      if (!backlink.__typename) return null;

      switch (backlink.__typename) {
        case 'Post':
          return allSlimPostsMap[backlink.id];
        case 'User':
          return allUsersMap[backlink.id];
        case 'Group':
          return allGroupMap[backlink.id];
        case 'Topic':
          return allTopicMap[backlink.id];
        default:
          exhaustiveMatchGuard(backlink.__typename);
          return null;
      }
    })
    .filter(
      (backlink): backlink is NonNullable<typeof backlink> => backlink != null,
    );
};

type Backlink = ReturnType<typeof useBacklinksFromPost>[number];

interface RowProps {
  children: React.ReactNode;
  icon: React.ReactNode;
  to: string;
}
const Row = ({ children, icon, to }: RowProps) => (
  <Button
    to={to}
    className={styles.row}
    target="_blank"
    rel="noreferrer noopener"
  >
    {icon}
    <div className={styles.label}>{children}</div>
  </Button>
);

const isBacklinkArchived = (backlink: Backlink) => {
  switch (backlink.__typename) {
    case 'SlimPost':
      return backlink.archivedAt != null;
    case 'User':
      return backlink.deactivatedAt != null;
    default:
      return false;
  }
};

const isBacklinkDraft = (backlink: Backlink) => {
  switch (backlink.__typename) {
    case 'SlimPost':
      return backlink.publishedAt == null;
    default:
      return false;
  }
};

const isBacklinkActive = (backlink: Backlink) =>
  !isBacklinkArchived(backlink) && !isBacklinkDraft(backlink);

interface BacklinkRowProps {
  backlink: Backlink;
}

const BacklinkRow = ({ backlink }: BacklinkRowProps) => {
  switch (backlink.__typename) {
    case 'SlimPost':
      return (
        <HoverContentPreview.Hoverable type="post" id={backlink.id}>
          <Row
            to={postPath(backlink)}
            icon={
              <SVGIcon className={styles.icon} size={12}>
                {getPostIcon(backlink, true)}
              </SVGIcon>
            }
          >
            {backlink.title}
          </Row>
        </HoverContentPreview.Hoverable>
      );
    case 'User':
      return (
        <HoverContentPreview.Hoverable type="user" id={backlink.id}>
          <Row
            to={userPath(backlink)}
            icon={
              <SVGIcon size={12} className={styles.icon}>
                {userIcon}
              </SVGIcon>
            }
          >
            {backlink.name}
          </Row>
        </HoverContentPreview.Hoverable>
      );
    case 'Group':
      return (
        <HoverContentPreview.Hoverable type="group" id={backlink.id}>
          <Row
            to={groupPath(backlink)}
            icon={
              <SVGIcon size={12} className={styles.icon}>
                {groupIcon}
              </SVGIcon>
            }
          >
            {backlink.name}
          </Row>
        </HoverContentPreview.Hoverable>
      );
    case 'Topic':
      return (
        <HoverContentPreview.Hoverable type="topic" id={backlink.id}>
          <Row
            to={topicPath(backlink)}
            icon={<SVGIcon size={12}>{topicIcon}</SVGIcon>}
          >
            {backlink.name}
          </Row>
        </HoverContentPreview.Hoverable>
      );
    default:
      return null;
  }
};

interface ControlBarBacklinksProps {
  post: DocumentType<typeof POST_FRAGMENT>;
}

const ControlBarBacklinks = ({ post }: ControlBarBacklinksProps) => {
  const [isShowingInactiveBacklinks, setIsShowingInactiveBacklinks] =
    useState(false);

  const backlinks = useBacklinksFromPost(post);

  const activeBacklinks = backlinks.filter(isBacklinkActive);

  const inactiveBacklinks = backlinks.filter(
    (backlink) => !isBacklinkActive(backlink),
  );

  const inactiveBacklinksLabel = (() => {
    const hasDrafts = inactiveBacklinks.some(isBacklinkDraft);
    const hasArchived = inactiveBacklinks.some(isBacklinkArchived);

    if (hasDrafts && hasArchived) {
      return 'drafts and archived';
    }

    if (hasDrafts) {
      return 'drafts';
    }

    return 'archived';
  })();

  return (
    <HoverContentPreview>
      <div className={styles.container}>
        <div className={styles.title}>Backlinks</div>
        {activeBacklinks.map((backlink) => (
          <BacklinkRow key={backlink.id} backlink={backlink} />
        ))}
        {inactiveBacklinks.length > 0 && !isShowingInactiveBacklinks && (
          <div className={styles.showInactiveBacklinksButtonContainer}>
            <Button
              onClick={() => setIsShowingInactiveBacklinks(true)}
              className={styles.showInactiveBacklinksButton}
              variant="secondary"
            >
              Show {inactiveBacklinksLabel}
            </Button>
          </div>
        )}
        {isShowingInactiveBacklinks && (
          <div className={styles.inactiveBacklinksContainer}>
            <div className={styles.title}>{inactiveBacklinksLabel}</div>
            {inactiveBacklinks.map((backlink) => (
              <BacklinkRow key={backlink.id} backlink={backlink} />
            ))}
          </div>
        )}
      </div>
    </HoverContentPreview>
  );
};

export default ControlBarBacklinks;
